// Enhanced Google Calendar service with automatic authentication
// Uses browser-compatible REST API approach with Supabase integration

// Tipos para eventos de Google Calendar
export interface GoogleCalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  start: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  colorId?: string;
  status?: string;
}

export interface GoogleCalendarConfig {
  apiKey: string;
  clientId: string;
  calendarId: string;
}

class GoogleCalendarService {
  private config: GoogleCalendarConfig | null = null;
  private isInitialized = false;
  private accessToken: string | null = null;
  private userCalendars: any[] = [];
  private selectedCalendarId: string = 'primary';

  // Auto-inicializar con token existente de Supabase
  async initializeWithSupabase(supabase: any) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        console.log('No hay sesión activa para Google Calendar');
        return false;
      }

      // Si el usuario se autenticó con Google, usar ese token
      if (session.user?.app_metadata?.provider === 'google' || session.provider_token) {
        this.accessToken = session.provider_token || session.access_token;
        this.isInitialized = true;
        
        // Cargar calendarios del usuario
        await this.loadUserCalendars();
        
        console.log('Google Calendar inicializado automáticamente');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error inicializando Google Calendar con Supabase:', error);
      return false;
    }
  }

  // Inicializar el servicio con las credenciales (versión browser-compatible)
  async initialize(config: GoogleCalendarConfig) {
    try {
      this.config = config;
      this.isInitialized = true;
      
      console.log('Google Calendar service initialized (manual mode)');
      return true;
    } catch (error) {
      console.error('Error inicializando Google Calendar:', error);
      return false;
    }
  }

  // Cargar calendarios del usuario
  async loadUserCalendars() {
    if (!this.accessToken) {
      return [];
    }

    try {
      const response = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.userCalendars = data.items || [];
      
      return this.userCalendars.map(cal => ({
        id: cal.id,
        summary: cal.summary,
        description: cal.description,
        backgroundColor: cal.backgroundColor,
        foregroundColor: cal.foregroundColor,
        primary: cal.primary,
        accessRole: cal.accessRole,
      }));
    } catch (error) {
      console.error('Error cargando calendarios del usuario:', error);
      return [];
    }
  }

  // Obtener calendarios disponibles
  getUserCalendars() {
    return this.userCalendars;
  }

  // Cambiar calendario seleccionado
  setSelectedCalendar(calendarId: string) {
    this.selectedCalendarId = calendarId;
  }

  // Obtener calendario seleccionado
  getSelectedCalendarId() {
    return this.selectedCalendarId;
  }

  // Verificar si está inicializado
  private checkInitialization() {
    if (!this.isInitialized || !this.config) {
      throw new Error('Google Calendar service no está inicializado. Llama a initialize() primero.');
    }
  }

  // Obtener eventos de múltiples calendarios
  async getAllEvents(startDate: Date, endDate: Date): Promise<GoogleCalendarEvent[]> {
    if (!this.accessToken) {
      return this.getMockEvents(startDate, endDate);
    }

    try {
      const allEvents: GoogleCalendarEvent[] = [];
      
      // Obtener eventos de todos los calendarios del usuario
      for (const calendar of this.userCalendars) {
        if (calendar.accessRole === 'reader' || calendar.accessRole === 'writer' || calendar.accessRole === 'owner') {
          const events = await this.getEventsFromCalendar(calendar.id, startDate, endDate);
          
          // Agregar información del calendario a cada evento
          const eventsWithCalendar = events.map(event => ({
            ...event,
            calendarId: calendar.id,
            calendarName: calendar.summary,
            calendarColor: calendar.backgroundColor || '#1976D2',
          }));
          
          allEvents.push(...eventsWithCalendar);
        }
      }

      return allEvents.sort((a, b) => {
        const dateA = new Date(a.start.dateTime || a.start.date!);
        const dateB = new Date(b.start.dateTime || b.start.date!);
        return dateA.getTime() - dateB.getTime();
      });
    } catch (error) {
      console.error('Error obteniendo todos los eventos:', error);
      return this.getMockEvents(startDate, endDate);
    }
  }

  // Obtener eventos de un calendario específico
  async getEventsFromCalendar(calendarId: string, startDate: Date, endDate: Date): Promise<GoogleCalendarEvent[]> {
    if (!this.accessToken) {
      return [];
    }

    try {
      const url = new URL(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`);
      url.searchParams.append('timeMin', startDate.toISOString());
      url.searchParams.append('timeMax', endDate.toISOString());
      url.searchParams.append('singleEvents', 'true');
      url.searchParams.append('orderBy', 'startTime');
      url.searchParams.append('maxResults', '100');

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          console.warn(`Sin acceso al calendario ${calendarId}`);
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.error(`Error obteniendo eventos del calendario ${calendarId}:`, error);
      return [];
    }
  }

  // Obtener eventos de un rango de fechas (versión browser usando REST API)
  async getEvents(startDate: Date, endDate: Date): Promise<GoogleCalendarEvent[]> {
    if (!this.isInitialized) {
      return this.getMockEvents(startDate, endDate);
    }

    // Si tenemos access token, obtener eventos reales de todos los calendarios
    if (this.accessToken) {
      return this.getAllEvents(startDate, endDate);
    }

    // Fallback a eventos mock
    return this.getMockEvents(startDate, endDate);
  }

  // Generar eventos mock para testing
  private getMockEvents(startDate: Date, endDate: Date): GoogleCalendarEvent[] {
    const mockEvents: GoogleCalendarEvent[] = [
      {
        id: 'mock-1',
        summary: 'Reunión de equipo',
        description: 'Reunión semanal del equipo de desarrollo',
        start: {
          dateTime: new Date(startDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        },
        end: {
          dateTime: new Date(startDate.getTime() + 2 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000).toISOString(),
        },
      },
      {
        id: 'mock-2',
        summary: 'Entrega de proyecto',
        description: 'Fecha límite para entrega del proyecto TaskManager',
        start: {
          date: new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        },
        end: {
          date: new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        },
      },
    ];

    return mockEvents.filter(event => {
      const eventDate = new Date(event.start.dateTime || event.start.date!);
      return eventDate >= startDate && eventDate <= endDate;
    });
  }

  // Crear un nuevo evento (versión browser)
  async createEvent(event: GoogleCalendarEvent): Promise<string | null> {
    this.checkInitialization();

    try {
      if (!this.accessToken) {
        console.warn('No access token disponible para crear eventos');
        return `mock-created-${Date.now()}`;
      }

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${this.config!.calendarId}/events`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(event),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.id;
    } catch (error) {
      console.error('Error creando evento en Google Calendar:', error);
      return `mock-created-${Date.now()}`;
    }
  }

  // Actualizar un evento existente (versión browser)
  async updateEvent(eventId: string, event: GoogleCalendarEvent): Promise<boolean> {
    this.checkInitialization();

    try {
      if (!this.accessToken) {
        console.warn('No access token disponible para actualizar eventos');
        return true; // Mock success
      }

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${this.config!.calendarId}/events/${eventId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(event),
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error actualizando evento en Google Calendar:', error);
      return true; // Mock success for demo
    }
  }

  // Eliminar un evento (versión browser)
  async deleteEvent(eventId: string): Promise<boolean> {
    this.checkInitialization();

    try {
      if (!this.accessToken) {
        console.warn('No access token disponible para eliminar eventos');
        return true; // Mock success
      }

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${this.config!.calendarId}/events/${eventId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          },
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error eliminando evento de Google Calendar:', error);
      return true; // Mock success for demo
    }
  }

  // Sincronizar tarea con Google Calendar
  async syncTaskToCalendar(task: {
    id: string;
    title: string;
    description?: string;
    deadline?: string;
    priority?: string;
    status?: string;
  }): Promise<string | null> {
    this.checkInitialization();

    if (!task.deadline) {
      console.warn('No se puede sincronizar tarea sin fecha límite:', task.title);
      return null;
    }

    try {
      const startDate = new Date(task.deadline);
      const endDate = new Date(startDate);
      endDate.setHours(startDate.getHours() + 1); // Duración de 1 hora por defecto

      const event: GoogleCalendarEvent = {
        summary: `[TaskManager] ${task.title}`,
        description: task.description ? 
          `${task.description}\n\nPrioridad: ${task.priority || 'Media'}\nEstado: ${task.status || 'Pendiente'}\nID: ${task.id}` :
          `Prioridad: ${task.priority || 'Media'}\nEstado: ${task.status || 'Pendiente'}\nID: ${task.id}`,
        start: {
          dateTime: startDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: endDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        colorId: this.getPriorityColorId(task.priority),
      };

      return await this.createEvent(event);
    } catch (error) {
      console.error('Error sincronizando tarea con Google Calendar:', error);
      return null;
    }
  }

  // Obtener color según la prioridad
  private getPriorityColorId(priority?: string): string {
    switch (priority?.toLowerCase()) {
      case 'alta':
        return '11'; // Rojo
      case 'media':
        return '5'; // Amarillo
      case 'baja':
        return '10'; // Verde
      default:
        return '1'; // Azul (default)
    }
  }

  // Obtener eventos del mes actual
  async getCurrentMonthEvents(): Promise<GoogleCalendarEvent[]> {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return this.getEvents(startOfMonth, endOfMonth);
  }

  // Verificar conectividad con Google Calendar (versión browser)
  async testConnection(): Promise<boolean> {
    this.checkInitialization();

    try {
      if (!this.accessToken && !this.config!.apiKey) {
        console.warn('No access token o API key disponible para test');
        return true; // Mock success for demo
      }

      const url = `https://www.googleapis.com/calendar/v3/calendars/${this.config!.calendarId}`;
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (this.accessToken) {
        headers.Authorization = `Bearer ${this.accessToken}`;
      } else if (this.config!.apiKey) {
        // Para calendarios públicos
        const urlWithKey = `${url}?key=${this.config!.apiKey}`;
        const response = await fetch(urlWithKey);
        return response.ok;
      }

      const response = await fetch(url, { headers });
      return response.ok;
    } catch (error) {
      console.error('Error probando conexión con Google Calendar:', error);
      return true; // Mock success for demo
    }
  }

  // Obtener información del calendario (versión browser)
  async getCalendarInfo(): Promise<any> {
    this.checkInitialization();

    try {
      if (!this.accessToken && !this.config!.apiKey) {
        return {
          id: this.config!.calendarId,
          summary: 'Calendario de prueba',
          description: 'Calendario de demostración',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      }

      // Implementación real para cuando se tenga access token
      const url = `https://www.googleapis.com/calendar/v3/calendars/${this.config!.calendarId}`;
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (this.accessToken) {
        headers.Authorization = `Bearer ${this.accessToken}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error('Error obteniendo info del calendario');
      }

      const data = await response.json();
      return {
        id: data.id,
        summary: data.summary,
        description: data.description,
        timeZone: data.timeZone,
      };
    } catch (error) {
      console.error('Error obteniendo información del calendario:', error);
      return {
        id: this.config!.calendarId,
        summary: 'Calendario de prueba',
        description: 'Calendario de demostración',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    }
  }

  // Métodos para autenticación OAuth2 (versión browser)
  initializeOAuth2(clientId: string, redirectUri: string) {
    const scope = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_type=code&` +
      `access_type=offline`;

    return { authUrl };
  }

  async setOAuth2Credentials(accessToken: string) {
    try {
      this.accessToken = accessToken;
      this.isInitialized = true;
      
      return { access_token: accessToken };
    } catch (error) {
      console.error('Error configurando credenciales OAuth2:', error);
      return null;
    }
  }
}

// Instancia singleton del servicio
export const googleCalendarService = new GoogleCalendarService();

// Configuración por defecto (se debe sobrescribir con credenciales reales)
export const defaultGoogleCalendarConfig: Partial<GoogleCalendarConfig> = {
  // Estas credenciales deben ser configuradas por el usuario
  // apiKey: 'TU_API_KEY_AQUI',
  // clientId: 'TU_CLIENT_ID_AQUI',
  // calendarId: 'primary' // o el ID del calendario específico
};

// Funciones de utilidad
export const formatTaskForCalendar = (task: any): GoogleCalendarEvent => {
  const deadline = task.deadline ? new Date(task.deadline) : new Date();
  const endTime = new Date(deadline);
  endTime.setHours(deadline.getHours() + 1);

  return {
    summary: task.title,
    description: task.description || '',
    start: {
      dateTime: deadline.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: endTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  };
};

export const isGoogleCalendarConfigured = (): boolean => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  
  return !!(apiKey && apiKey !== 'your_api_key_here' && 
           clientId && clientId !== 'your_client_id_here');
};

export const getGoogleCalendarConfig = () => ({
  apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
  calendarId: process.env.REACT_APP_GOOGLE_CALENDAR_ID || 'primary',
  redirectUri: process.env.REACT_APP_GOOGLE_REDIRECT_URI || 'http://localhost:3000/auth/callback'
});

export default GoogleCalendarService;